import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { User, Shield, Lock, AlertTriangle, X, Eye, EyeOff, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: "account", label: "Compte", icon: User },
  { id: "security", label: "Sécurité", icon: Shield },
  { id: "privacy", label: "Confidentialité", icon: Lock },
  { id: "danger", label: "Zone dangereuse", icon: AlertTriangle },
];

// ── Account Tab ──────────────────────────────────────────────────────────────
function AccountTab({ user }) {
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
  });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess(false);
    try {
      await axios.put(`${API}/api/auth/update-account`, form, { withCredentials: true });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <h2 className="font-outfit font-semibold text-white text-xl mb-2">Informations du compte</h2>
      <p className="text-cbm-muted text-sm mb-6">Gérez vos informations personnelles</p>

      <form onSubmit={handleSave} className="cbm-card p-6 space-y-4">
        <div>
          <label className="cbm-label block mb-1.5">Nom complet</label>
          <input
            type="text"
            className="cbm-input"
            value={form.name}
            onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
            required
          />
        </div>

        <div>
          <label className="cbm-label block mb-1.5">Email</label>
          <input
            type="email"
            className="cbm-input"
            value={form.email}
            onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
            required
          />
          <p className="text-cbm-muted text-xs mt-1.5">Utilisé pour la connexion et les notifications</p>
        </div>

        {error && <div className="bg-red-900/20 border border-red-800/30 text-red-400 rounded-lg px-4 py-2.5 text-sm">{error}</div>}
        {success && (
          <div className="bg-green-900/20 border border-green-800/30 text-green-400 rounded-lg px-4 py-2.5 text-sm flex items-center gap-2">
            <CheckCircle2 size={16} /> Modifications enregistrées
          </div>
        )}

        <button type="submit" disabled={saving} className="cbm-btn-primary text-sm">
          {saving ? "Enregistrement..." : "Enregistrer"}
        </button>
      </form>
    </div>
  );
}

// ── Security Tab ──────────────────────────────────────────────────────────────
function SecurityTab() {
  const [form, setForm] = useState({ old_password: "", new_password: "", confirm_password: "" });
  const [showPasswords, setShowPasswords] = useState({ old: false, new: false, confirm: false });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      setError("Les nouveaux mots de passe ne correspondent pas");
      return;
    }
    if (form.new_password.length < 8) {
      setError("Le nouveau mot de passe doit contenir au moins 8 caractères");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess(false);
    try {
      await axios.put(`${API}/api/auth/change-password`, {
        old_password: form.old_password,
        new_password: form.new_password,
      }, { withCredentials: true });
      setSuccess(true);
      setForm({ old_password: "", new_password: "", confirm_password: "" });
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors du changement de mot de passe");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <h2 className="font-outfit font-semibold text-white text-xl mb-2">Sécurité</h2>
      <p className="text-cbm-muted text-sm mb-6">Gérez votre mot de passe et la sécurité de votre compte</p>

      <form onSubmit={handleChangePassword} className="cbm-card p-6 space-y-4">
        <div>
          <label className="cbm-label block mb-1.5">Mot de passe actuel</label>
          <div className="relative">
            <input
              type={showPasswords.old ? "text" : "password"}
              className="cbm-input pr-10"
              value={form.old_password}
              onChange={(e) => setForm(f => ({ ...f, old_password: e.target.value }))}
              required
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-cbm-muted hover:text-white"
              onClick={() => setShowPasswords(s => ({ ...s, old: !s.old }))}
            >
              {showPasswords.old ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        <div>
          <label className="cbm-label block mb-1.5">Nouveau mot de passe</label>
          <div className="relative">
            <input
              type={showPasswords.new ? "text" : "password"}
              className="cbm-input pr-10"
              value={form.new_password}
              onChange={(e) => setForm(f => ({ ...f, new_password: e.target.value }))}
              required
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-cbm-muted hover:text-white"
              onClick={() => setShowPasswords(s => ({ ...s, new: !s.new }))}
            >
              {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <p className="text-cbm-muted text-xs mt-1">Minimum 8 caractères</p>
        </div>

        <div>
          <label className="cbm-label block mb-1.5">Confirmer le nouveau mot de passe</label>
          <div className="relative">
            <input
              type={showPasswords.confirm ? "text" : "password"}
              className="cbm-input pr-10"
              value={form.confirm_password}
              onChange={(e) => setForm(f => ({ ...f, confirm_password: e.target.value }))}
              required
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-cbm-muted hover:text-white"
              onClick={() => setShowPasswords(s => ({ ...s, confirm: !s.confirm }))}
            >
              {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        {error && <div className="bg-red-900/20 border border-red-800/30 text-red-400 rounded-lg px-4 py-2.5 text-sm">{error}</div>}
        {success && (
          <div className="bg-green-900/20 border border-green-800/30 text-green-400 rounded-lg px-4 py-2.5 text-sm flex items-center gap-2">
            <CheckCircle2 size={16} /> Mot de passe modifié avec succès
          </div>
        )}

        <button type="submit" disabled={saving} className="cbm-btn-primary text-sm">
          {saving ? "Modification..." : "Changer le mot de passe"}
        </button>
      </form>
    </div>
  );
}

// ── Privacy Tab ──────────────────────────────────────────────────────────────
function PrivacyTab({ profile }) {
  const [emailVisible, setEmailVisible] = useState(profile?.email_visible ?? true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleToggleEmailVisibility = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/profiles/me`, { email_visible: !emailVisible }, { withCredentials: true });
      setEmailVisible(!emailVisible);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      alert("Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <h2 className="font-outfit font-semibold text-white text-xl mb-2">Confidentialité</h2>
      <p className="text-cbm-muted text-sm mb-6">Contrôlez la visibilité de vos informations</p>

      <div className="cbm-card p-6 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-white font-medium mb-1">Visibilité de l'email</p>
            <p className="text-cbm-muted text-sm">
              {emailVisible ? "Votre email est visible par les autres membres" : "Votre email est masqué"}
            </p>
          </div>
          <button
            onClick={handleToggleEmailVisibility}
            disabled={saving}
            className={`relative w-12 h-6 rounded-full transition-colors ${emailVisible ? "bg-gold" : "bg-cbm-muted/30"}`}
          >
            <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${emailVisible ? "translate-x-6" : ""}`} />
          </button>
        </div>

        {success && (
          <div className="bg-green-900/20 border border-green-800/30 text-green-400 rounded-lg px-4 py-2.5 text-sm flex items-center gap-2">
            <CheckCircle2 size={16} /> Préférence enregistrée
          </div>
        )}

        <div className="pt-4 border-t cbm-divider">
          <p className="text-white font-medium mb-1">Export de données</p>
          <p className="text-cbm-muted text-sm mb-3">Téléchargez une copie de vos données personnelles</p>
          <button className="cbm-btn-ghost text-sm" disabled>
            Exporter mes données (bientôt disponible)
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Danger Zone Tab ──────────────────────────────────────────────────────────
function DangerZoneTab() {
  const { logout } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    if (confirmText !== "SUPPRIMER") return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/auth/delete-account`, { withCredentials: true });
      logout();
    } catch (err) {
      alert("Erreur lors de la suppression du compte");
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <h2 className="font-outfit font-semibold text-white text-xl mb-2">Zone dangereuse</h2>
      <p className="text-cbm-muted text-sm mb-6">Ces actions sont irréversibles</p>

      <div className="p-6 rounded-xl" style={{ background: "rgba(220, 38, 38, 0.08)", border: "1px solid rgba(220, 38, 38, 0.3)" }}>
        <p className="text-white font-medium mb-2">Supprimer mon compte</p>
        <p className="text-cbm-muted text-sm mb-4">
          Une fois supprimé, votre compte et toutes vos données seront définitivement perdus. Cette action est irréversible.
        </p>
        <button
          onClick={() => setShowModal(true)}
          className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
          style={{ background: "#DC2626", color: "white" }}
          onMouseOver={(e) => { e.target.style.background = "#B91C1C"; }}
          onMouseOut={(e) => { e.target.style.background = "#DC2626"; }}
        >
          Supprimer mon compte
        </button>
      </div>

      {/* Delete Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center animate-fade-in">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowModal(false)} />
          <div className="relative z-10 w-full max-w-md mx-4 cbm-card rounded-2xl p-6 shadow-gold-strong animate-fade-in-up">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-outfit font-semibold text-white text-lg flex items-center gap-2">
                <AlertTriangle size={20} className="text-red-400" />
                Supprimer le compte
              </h3>
              <button onClick={() => setShowModal(false)} className="text-cbm-muted hover:text-white">
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-cbm-secondary text-sm">
                Cette action est <strong className="text-red-400">irréversible</strong>. Toutes vos données seront supprimées définitivement.
              </p>

              <div>
                <label className="cbm-label block mb-1.5">
                  Pour confirmer, tapez <span className="text-red-400 font-mono">SUPPRIMER</span>
                </label>
                <input
                  type="text"
                  className="cbm-input"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="SUPPRIMER"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  className="cbm-btn-ghost text-sm flex-1"
                  onClick={() => { setShowModal(false); setConfirmText(""); }}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  disabled={confirmText !== "SUPPRIMER" || deleting}
                  onClick={handleDeleteAccount}
                  className="flex-1 text-sm py-2.5 px-4 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: confirmText === "SUPPRIMER" ? "#DC2626" : "#4B5563", color: "white" }}
                  onMouseOver={(e) => { if (confirmText === "SUPPRIMER") e.target.style.background = "#B91C1C"; }}
                  onMouseOut={(e) => { if (confirmText === "SUPPRIMER") e.target.style.background = "#DC2626"; }}
                >
                  {deleting ? "Suppression..." : "Supprimer définitivement"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────────────
export default function ParametresPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("account");
  const [profile, setProfile] = useState(null);

  // Fetch profile for privacy settings
  useState(() => {
    axios.get(`${API}/api/profiles/me`, { withCredentials: true })
      .then(res => setProfile(res.data))
      .catch(() => {});
  }, []);

  return (
    <div className="p-6 max-w-7xl mx-auto animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(201,162,39,0.15)", border: "1px solid rgba(201,162,39,0.3)" }}>
            <User size={16} className="text-gold" />
          </div>
          <div>
            <h1 className="cbm-section-title leading-tight">Paramètres</h1>
            <p className="text-cbm-muted text-xs mt-0.5">Gérez votre compte et vos préférences</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b cbm-divider pb-0 overflow-x-auto">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all border-b-2 whitespace-nowrap ${
              tab === id
                ? "border-gold text-white"
                : "border-transparent text-cbm-muted hover:text-white hover:border-white/10"
            }`}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="animate-fade-in">
        {tab === "account" && <AccountTab user={user} />}
        {tab === "security" && <SecurityTab />}
        {tab === "privacy" && <PrivacyTab profile={profile} />}
        {tab === "danger" && <DangerZoneTab />}
      </div>
    </div>
  );
}
